// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * DashboardAdvisorPerformance Component
 *
 * Admin-only component displaying advisor performance metrics in a sortable table.
 */

import { useState } from 'react';
import { AppIcon, Badge, Skeleton } from '@loa/ui-components';
import type { AdvisorMetric, SortColumn, SortDirection } from '../../types/dashboard';

export type DashboardAdvisorPerformanceProps = Readonly<{
  /** Advisor metrics to display */
  advisors: AdvisorMetric[];
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string | null;
  /** Handler for clicking an advisor row */
  onAdvisorClick?: (advisor: AdvisorMetric) => void;
  /** Handler for sort change */
  onSortChange?: (column: SortColumn, direction: SortDirection) => void;
}>;

export type { SortColumn, SortDirection } from '../../types/dashboard';

// ============================================
// Column Configuration
// ============================================

interface ColumnConfig {
  key: SortColumn | 'advisorName';
  label: string;
  sortable: boolean;
  textAlign?: 'left' | 'center' | 'right';
}

const columns: ColumnConfig[] = [
  { key: 'advisorName', label: 'Advisor', sortable: false, textAlign: 'left' },
  { key: 'profileCount', label: 'Profiles', sortable: true, textAlign: 'center' },
  { key: 'clientCount', label: 'Clients', sortable: true, textAlign: 'center' },
  { key: 'pendingLoas', label: 'Pending', sortable: true, textAlign: 'center' },
  { key: 'completionRate', label: 'Rate', sortable: true, textAlign: 'center' },
];

// ============================================
// Helper Functions
// ============================================

function formatCompletionRate(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function getCompletionRateColor(rate: number): 'green' | 'yellow' | 'orange' | 'red' {
  if (rate >= 0.9) return 'green';
  if (rate >= 0.7) return 'yellow';
  if (rate >= 0.5) return 'orange';
  return 'red';
}

function getSortIcon(
  column: SortColumn | 'advisorName',
  currentSortColumn: SortColumn | null,
  currentDirection: SortDirection
): string | undefined {
  if (column === 'advisorName') return undefined;
  if (currentSortColumn !== column) return 'sort';
  return currentDirection === 'asc' ? 'sort up' : 'sort down';
}

// ============================================
// Main Component
// ============================================

export function DashboardAdvisorPerformance({
  advisors,
  loading = false,
  error = null,
  onAdvisorClick,
  onSortChange,
}: DashboardAdvisorPerformanceProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>('profileCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (column: SortColumn) => {
    let newDirection: SortDirection = 'desc';

    if (sortColumn === column) {
      // Toggle direction if same column
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(column);
    setSortDirection(newDirection);
    onSortChange?.(column, newDirection);
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem' }}>
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '1em' }}>
          <AppIcon name="users" />
          Advisor Performance
        </h4>
        <Skeleton style={{ height: '1em', marginBottom: '0.5em' }} />
        <Skeleton style={{ height: '1em', marginBottom: '0.5em' }} />
        <Skeleton style={{ height: '1em', marginBottom: '0.5em' }} />
        <Skeleton style={{ height: '1em' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '1em' }}>
          <AppIcon name="users" />
          Advisor Performance
        </h4>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <AppIcon name="warning sign" color="orange" />
          <p style={{ fontWeight: 500 }}>Unable to load advisor metrics</p>
          <p style={{ color: '#666' }}>{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (advisors.length === 0) {
    return (
      <div style={{ padding: '1rem' }}>
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '1em' }}>
          <AppIcon name="users" />
          Advisor Performance
        </h4>
        <div style={{ textAlign: 'center', padding: '2rem', minHeight: '150px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <AppIcon name="user outline" color="grey" />
          <p style={{ fontWeight: 500 }}>No Advisor Data</p>
          <p style={{ color: '#666' }}>Advisor metrics will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <section aria-label="Advisor performance metrics" style={{ padding: '1rem' }}>
      <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '1em' }}>
        <AppIcon name="users" />
        Advisor Performance
        <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#666', marginLeft: '0.5em' }}>
          {advisors.length} Advisors
        </span>
      </h4>

      <div style={{ overflowX: 'auto' }}>
        <table className="fin-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const isSortableButton = col.sortable && col.key !== 'advisorName';
                const sortIcon = isSortableButton && getSortIcon(col.key, sortColumn, sortDirection);
                let ariaSort: 'ascending' | 'descending' | undefined;
                if (col.sortable && sortColumn === col.key) {
                  ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending';
                }
                let sortStateLabel = '';
                if (sortColumn === col.key) {
                  sortStateLabel = sortDirection === 'asc' ? ', currently ascending' : ', currently descending';
                }
                return (
                  <th
                    key={col.key}
                    style={{
                      textAlign: col.textAlign,
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                    aria-sort={ariaSort}
                  >
                    {isSortableButton ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col.key as SortColumn)}
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer', color: 'inherit' }}
                        aria-label={`Sort by ${col.label}${sortStateLabel}`}
                      >
                        {col.label}
                        {sortIcon && (
                          <AppIcon
                            name={sortIcon}
                            style={{ marginLeft: '0.25em', opacity: sortColumn === col.key ? 1 : 0.3 }}
                          />
                        )}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {advisors.map((advisor) => (
              <AdvisorRow
                key={advisor.advisorId}
                advisor={advisor}
                onClick={onAdvisorClick ? () => onAdvisorClick(advisor) : undefined}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================
// Advisor Row Sub-component
// ============================================

type AdvisorRowProps = Readonly<{
  advisor: AdvisorMetric;
  onClick?: () => void;
}>;

function AdvisorRow({ advisor, onClick }: AdvisorRowProps) {
  const isClickable = !!onClick;

  return (
    <tr
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{ cursor: isClickable ? 'pointer' : 'default' }}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={`${advisor.advisorName}: ${advisor.profileCount} profiles, ${advisor.clientCount} clients, ${advisor.pendingLoas} pending LOAs, ${formatCompletionRate(advisor.completionRate)} completion rate`}
    >
      {/* Advisor Name & Email */}
      <td>
        <div style={{ fontWeight: 500 }}>{advisor.advisorName}</div>
        <div style={{ fontSize: '0.85em', color: '#666' }}>{advisor.advisorEmail}</div>
      </td>

      {/* Profile Count */}
      <td style={{ textAlign: 'center' }}>
        <span style={{ fontWeight: 500 }}>{advisor.profileCount}</span>
      </td>

      {/* Client Count */}
      <td style={{ textAlign: 'center' }}>
        <span style={{ fontWeight: 500 }}>{advisor.clientCount}</span>
      </td>

      {/* Pending LOAs */}
      <td style={{ textAlign: 'center' }}>
        {advisor.pendingLoas > 0 ? (
          <Badge color={advisor.pendingLoas > 10 ? 'orange' : 'blue'} size="sm">
            {advisor.pendingLoas}
          </Badge>
        ) : (
          <span style={{ color: '#999' }}>0</span>
        )}
      </td>

      {/* Completion Rate */}
      <td style={{ textAlign: 'center' }}>
        <Badge
          color={getCompletionRateColor(advisor.completionRate)}
          size="sm"
          style={{ minWidth: '3.5em' }}
        >
          {formatCompletionRate(advisor.completionRate)}
        </Badge>
      </td>
    </tr>
  );
}

export default DashboardAdvisorPerformance;
